import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, User, Heart, ShoppingBag, Menu, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { ASSETS, BRAND } from "@/lib/constants";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Our Products", to: "/shop" },
  { label: "About Us", to: "/#about" },
  { label: "Why Dignity", to: "/#why" },
  { label: "Contact", to: "/#contact" },
];

export default function Navbar() {
  const { cartCount, setCartOpen, setSearchOpen, wishlist } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "glass py-2.5 shadow-lg shadow-black/40" : "bg-transparent py-4"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={ASSETS.logo} alt="Dignity Food Kitchen logo" className="h-11 w-11 rounded-full object-cover ring-1 ring-[#D4AF37]/40" />
            <div className="hidden sm:block leading-none">
              <div className="font-display text-xl tracking-wide text-cream">DIGNITY</div>
              <div className="font-oswald text-[9px] uppercase tracking-[0.3em] text-gold">Food Kitchen</div>
            </div>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                className="font-oswald text-[13px] uppercase tracking-[0.12em] text-white/75 transition-colors hover:text-gold"
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <button onClick={() => setSearchOpen(true)} aria-label="Search" data-testid="search-btn"
              className="grid h-9 w-9 place-items-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-gold">
              <Search size={18} />
            </button>
            <button onClick={() => navigate("/#reviews")} aria-label="Account" data-testid="account-btn"
              className="hidden sm:grid h-9 w-9 place-items-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-gold">
              <User size={18} />
            </button>
            <Link to="/shop" aria-label="Wishlist" data-testid="wishlist-btn"
              className="relative hidden sm:grid h-9 w-9 place-items-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-gold">
              <Heart size={18} />
              {wishlist.length > 0 && (
                <span className="absolute -right-0.5 -top-0.5 grid h-4 w-4 place-items-center rounded-full bg-red-500 text-[9px] font-bold text-white">{wishlist.length}</span>
              )}
            </Link>
            <button onClick={() => setCartOpen(true)} aria-label="Cart" data-testid="cart-btn"
              className="relative grid h-9 w-9 place-items-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-gold">
              <ShoppingBag size={18} />
              {cartCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 grid h-4 w-4 place-items-center rounded-full gold-gradient text-[9px] font-bold text-black">{cartCount}</span>
              )}
            </button>

            <Link to="/shop" data-testid="shop-now-btn"
              className="ml-2 hidden rounded-full gold-gradient px-5 py-2.5 font-oswald text-xs uppercase tracking-[0.15em] text-black transition-transform hover:scale-105 md:block">
              Shop Now
            </Link>

            <button onClick={() => setMobileOpen(true)} aria-label="Menu" data-testid="mobile-menu-btn"
              className="grid h-9 w-9 place-items-center rounded-full text-white/80 lg:hidden">
              <Menu size={20} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] lg:hidden">
            <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
              className="absolute right-0 top-0 h-full w-4/5 max-w-sm bg-forest-deep p-7">
              <div className="flex items-center justify-between">
                <span className="font-display text-2xl text-cream">MENU</span>
                <button onClick={() => setMobileOpen(false)} aria-label="Close menu"><X className="text-white/80" /></button>
              </div>
              <div className="mt-10 flex flex-col gap-5">
                {NAV.map((n) => (
                  <Link key={n.label} to={n.to} onClick={() => setMobileOpen(false)}
                    className="font-oswald text-2xl uppercase tracking-wide text-white/80 hover:text-gold">
                    {n.label}
                  </Link>
                ))}
              </div>
              <Link to="/shop" onClick={() => setMobileOpen(false)}
                className="mt-10 block rounded-full gold-gradient px-6 py-3 text-center font-oswald uppercase tracking-wider text-black">
                Shop Now
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
